import React from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
// Parooli tugevuse arvutamise funktsioon
const calculatePasswordStrength = (password) => {
    if (!password) {
        return {
            score: 0,
            level: 'none',
            color: '#gray',
            text: 'Enter password',
            timeToHack: '',
            requirements: {
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                symbol: false
            }
        };
    }

    const length = password.length;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const requirements = {
        length: length >= 12,
        uppercase: hasUppercase,
        lowercase: hasLowercase,
        number: hasNumber
    };

    // Determine password category
    let category = '';
    if (length <= 6) {
        category = 'numbers_only'; // Weakest
    } else if (hasLowercase && !hasUppercase && !hasNumber) {
        category = 'lowercase_only';
    } else if ((hasUppercase || hasLowercase) && !hasNumber) {
        category = 'letters_only';
    } else if ((hasUppercase || hasLowercase) && hasNumber) {
        category = 'letters_numbers';
    } else {
        category = 'letters_numbers'; // default
    }

    // Arvutame murdemise aja ja tugevuse
    const hackTime = getHackTime(length, category);
    const strength = getStrengthLevel(hackTime, length, requirements);

    return {
        score: strength.score,
        level: strength.level,
        color: strength.color,
        text: strength.text,
        timeToHack: hackTime.display,
        requirements: requirements,
        category: category
    };
};

// Murdemise aja arvutamine tabeli põhjal
const getHackTime = (length, category) => {
    const hackTimes = {
        4: { numbers_only: 'Instantly', lowercase_only: 'Instantly', letters_only: 'Instantly', letters_numbers: 'Instantly' },
        5: { numbers_only: 'Instantly', lowercase_only: 'Instantly', letters_only: 'Instantly', letters_numbers: 'Instantly' },
        6: { numbers_only: 'Instantly', lowercase_only: 'Instantly', letters_only: 'Instantly', letters_numbers: 'Instantly' },
        7: { numbers_only: 'Instantly', lowercase_only: 'Instantly', letters_only: '1 sec', letters_numbers: '2 secs' },
        8: { numbers_only: 'Instantly', lowercase_only: 'Instantly', letters_only: '28 secs', letters_numbers: '2 mins' },
        9: { numbers_only: 'Instantly', lowercase_only: '3 secs', letters_only: '24 mins', letters_numbers: '2 hours' },
        10: { numbers_only: 'Instantly', lowercase_only: '1 min', letters_only: '21 hours', letters_numbers: '5 days' },
        11: { numbers_only: 'Instantly', lowercase_only: '32 mins', letters_only: '1 month', letters_numbers: '10 months' },
        12: { numbers_only: '1 sec', lowercase_only: '14 hours', letters_only: '6 years', letters_numbers: '53 years' },
        13: { numbers_only: '5 secs', lowercase_only: '2 weeks', letters_only: '332 years', letters_numbers: '3k years' },
        14: { numbers_only: '52 secs', lowercase_only: '1 year', letters_only: '17k years', letters_numbers: '202k years' },
        15: { numbers_only: '9 mins', lowercase_only: '27 years', letters_only: '898k years', letters_numbers: '12m years' },
        16: { numbers_only: '1 hour', lowercase_only: '713 years', letters_only: '46m years', letters_numbers: '779m years' },
        17: { numbers_only: '14 hours', lowercase_only: '18k years', letters_only: '2bn years', letters_numbers: '48bn years' },
        18: { numbers_only: '6 days', lowercase_only: '481k years', letters_only: '126bn years', letters_numbers: '2tn years' }
    };

    const maxLength = Math.min(length, 18);
    const time = hackTimes[maxLength]?.[category] || 'Unknown';

    return {
        display: time,
        raw: time
    };
};

// Tugevuse taseme määramine
const getStrengthLevel = (hackTime, length, requirements) => {
    const timeString = hackTime.display.toLowerCase();
    const requirementsMet = Object.values(requirements).filter(Boolean).length;

    // Very weak (red)
    if (length < 8 || timeString.includes('instantly') || (timeString.includes('sec') && !timeString.includes('min'))) {
        return {
            score: 1,
            level: 'very_weak',
            color: '#ff4d4f',
            text: 'Very Weak'
        };
    }

    // Weak (orange)
    if (length < 12 || requirementsMet < 3 || timeString.includes('min') || timeString.includes('hour')) {
        return {
            score: 2,
            level: 'weak',
            color: '#fa8c16',
            text: 'Weak'
        };
    }

    // Medium (yellow)
    if (length < 14 || requirementsMet < 4 || (timeString.includes('day') || timeString.includes('month') || (timeString.includes('year') && !timeString.includes('k') && !timeString.includes('m') && !timeString.includes('bn')))) {
        return {
            score: 3,
            level: 'medium',
            color: '#fadb14',
            text: 'Medium'
        };
    }

    // Strong (green)
    if (length >= 14 && requirementsMet >= 3 && (timeString.includes('k years') || timeString.includes('m years'))) {
        return {
            score: 4,
            level: 'strong',
            color: '#52c41a',
            text: 'Strong'
        };
    }

    // Very strong (dark green)
    if (length >= 16 && requirementsMet === 4 && (timeString.includes('bn years') || timeString.includes('tn years'))) {
        return {
            score: 5,
            level: 'very_strong',
            color: '#389e0d',
            text: 'Very Strong'
        };
    }

    return {
        score: 3,
        level: 'medium',
        color: '#fadb14',
        text: 'Medium'
    };
};

// Requirements component creation
const PasswordRequirements = ({ requirements, password }) => {
    const requirementsList = [
        { key: 'length', text: 'At least 12 characters', met: requirements.length },
        { key: 'uppercase', text: 'At least one uppercase letter (A-Z)', met: requirements.uppercase },
        { key: 'lowercase', text: 'At least one lowercase letter (a-z)', met: requirements.lowercase },
        { key: 'number', text: 'At least one number (0-9)', met: requirements.number }
    ];

    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Password requirements:
            </Typography>
            {requirementsList.map((req) => (
                <Box key={req.key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    {req.met ? (
                        <CheckCircle sx={{ color: '#52c41a', fontSize: 16, mr: 1 }} />
                    ) : (
                        <Box sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: '2px solid #d9d9d9',
                            mr: 1
                        }} />
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            color: req.met ? '#52c41a' : '#8c8c8c',
                            fontSize: '0.875rem'
                        }}
                    >
                        {req.text}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// Strength indicator component creation
const PasswordStrengthIndicator = ({ strength }) => {
    const getProgressWidth = () => {
        return (strength.score / 5) * 100;
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Password strength: <span style={{ color: strength.color }}>{strength.text}</span>
                </Typography>
                <Typography variant="body2" sx={{ color: '#8c8c8c', fontSize: '0.75rem' }}>
                    Time to hack: {strength.timeToHack}
                </Typography>
            </Box>
            <Box sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#f0f0f0',
                borderRadius: 4,
                overflow: 'hidden'
            }}>
                <Box sx={{
                    width: `${getProgressWidth()}%`,
                    height: '100%',
                    backgroundColor: strength.color,
                    transition: 'all 0.3s ease'
                }} />
            </Box>
        </Box>
    );
};

export { calculatePasswordStrength, PasswordRequirements, PasswordStrengthIndicator };